import { ApiErrorCode, type PodForbiddenBody } from '@dev-console/api/errors';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { GenericErrorModal, type GenericErrorPayload } from '@/components/modals/generic-error-modal';
import { JobsDlqErrorModal, type JobsDlqErrorPayload } from '@/components/modals/jobs-dlq-error-modal';
import { PodForbiddenModal } from '@/components/modals/pod-forbidden-modal';
import { ApiError } from '@/config/api';

type ActiveModal =
  | { type: 'pod-forbidden'; detail: PodForbiddenBody }
  | { type: 'generic-error'; payload: GenericErrorPayload }
  | { type: 'jobs-dlq-error'; payload: JobsDlqErrorPayload };

interface ModalContextValue {
  showPodForbidden: (detail: PodForbiddenBody) => void;
  showGenericError: (payload: GenericErrorPayload) => void;
  showJobsDlqError: (payload: JobsDlqErrorPayload) => void;
  // Catch-all: pass any caught error; dispatches to the best-fit modal.
  // `ApiError` with code POD_FORBIDDEN → pod-forbidden modal; any other
  // `ApiError` or plain `Error` → generic modal. Domain-specific callers
  // (e.g., the DLQ dialogs) should prefer `showJobsDlqError` for richer context.
  showForError: (err: unknown) => void;
}

const ModalContext = createContext<ModalContextValue | null>(null);

export const useModal = (): ModalContextValue => {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error('useModal must be used within <AppModalProvider>');
  return ctx;
};

export const AppModalProvider = ({ children }: { children: React.ReactNode }) => {
  const [active, setActive] = useState<ActiveModal | null>(null);
  const close = useCallback(() => setActive(null), []);

  const value = useMemo<ModalContextValue>(
    () => ({
      showPodForbidden: (detail) => setActive({ type: 'pod-forbidden', detail }),
      showGenericError: (payload) => setActive({ type: 'generic-error', payload }),
      showJobsDlqError: (payload) => setActive({ type: 'jobs-dlq-error', payload }),
      showForError: (err) => {
        if (err instanceof ApiError) {
          if (err.body.code === ApiErrorCode.POD_FORBIDDEN) {
            setActive({ type: 'pod-forbidden', detail: err.body });
          } else {
            setActive({ type: 'generic-error', payload: { status: err.status, message: err.body.message } });
          }
        } else {
          setActive({
            type: 'generic-error',
            payload: { status: 0, message: err instanceof Error ? err.message : String(err) },
          });
        }
      },
    }),
    []
  );

  return (
    <ModalContext.Provider value={value}>
      {children}
      {active?.type === 'pod-forbidden' && <PodForbiddenModal detail={active.detail} onClose={close} />}
      {active?.type === 'generic-error' && <GenericErrorModal payload={active.payload} onClose={close} />}
      {active?.type === 'jobs-dlq-error' && <JobsDlqErrorModal payload={active.payload} onClose={close} />}
    </ModalContext.Provider>
  );
};
